import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { devWarn } from "@/lib/logger";

/**
 * State + effects for the "Undo import" banner.
 *
 * - Polls `/csv-import/batches/latest` on mount + every 15 s.
 * - Ticks the countdown every second.
 * - Listens for the `csv-import-complete` window event so the banner
 *   refreshes immediately after the wizard applies.
 *
 * The hook is called only for internal users; other org types get a no-op
 * `batch = null` / `remaining = 0` state and no network traffic.
 */
export default function useUndoImportBatch(user) {
  const [batch, setBatch] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const pollRef = useRef(null);
  const enabled = !!user && user.org_type === "internal";

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get("/csv-import/batches/latest");
      const b = data?.batch;
      if (b && b.undoable && !b.undone && b.seconds_remaining > 0) {
        setBatch(b);
        setRemaining(b.seconds_remaining);
      } else {
        setBatch(null);
        setRemaining(0);
      }
    } catch (err) {
      devWarn("undo-import banner poll failed:", err?.message || err);
    }
  }, [enabled]);

  // Initial load + 15 s poll (server-authoritative).
  useEffect(() => {
    if (!enabled) return undefined;
    load();
    pollRef.current = setInterval(load, 15000);
    return () => clearInterval(pollRef.current);
  }, [enabled, load]);

  // 1 s local countdown tick.
  useEffect(() => {
    const t = setInterval(() => {
      setRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // Wizard broadcast → refresh instantly, don't wait for the 15 s poll.
  useEffect(() => {
    const onImportComplete = () => load();
    window.addEventListener("csv-import-complete", onImportComplete);
    return () => window.removeEventListener("csv-import-complete", onImportComplete);
  }, [load]);

  const undo = useCallback(async () => {
    if (!batch) return;
    const rowCount = batch.row_count;
    const ok = window.confirm(
      `Undo import of ${rowCount} lead${rowCount === 1 ? "" : "s"}?\n\n` +
      "This deletes the leads and all their timeline events. " +
      "If any lead has already been delivered to an external CRM, the undo is refused.",
    );
    if (!ok) return;

    setBusy(true);
    try {
      const { data } = await api.post(`/csv-import/batches/${batch.id}/undo`);
      toast.success(`Undone — ${data.deleted} lead${data.deleted === 1 ? "" : "s"} removed`);
      setBatch(null);
      setRemaining(0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Undo failed");
    } finally {
      setBusy(false);
    }
  }, [batch]);

  const dismiss = useCallback(() => setDismissed(true), []);

  const visible = !!batch && !dismissed && remaining > 0;

  return { visible, batch, remaining, busy, undo, dismiss };
}
